import {Injectable} from '@angular/core';
import {TableOfContentsComponent} from '../../components/widgets/table-of-contents/table-of-contents.component';
import {ConfigService} from '../config/config.service';
import {GoogleAnalyticsService} from '../googleAnalytics/google-analytics.service';
import {BehaviorSubject} from 'rxjs';
import {AttributeTableEntryComponent} from '../../components/widgets/attribute-table/attribute-table-entry.component';
import {IdentifyEntryComponent} from '../../components/widgets/identify/identify-entry.component';
import {LegendEntryComponent} from '../../components/widgets/legend/legend-entry.component';
import {AddDataEntryComponent} from '../../components/widgets/add-data/add-data-entry.component';
import {BasemapGalleryEntryComponent} from '../../components/widgets/basemap-gallery/basemap-gallery-entry.component';
import {DrawEntryComponent} from '../../components/widgets/draw/draw-entry.component';
import {SelectEntryComponent} from '../../components/widgets/select/select-entry.component';
import {FindLocationEntryComponent} from '../../components/widgets/find-location/find-location-entry.component';
import {FeedbackEntryComponent} from '../../components/widgets/feedback/feedback-entry.component';
import {MeasureEntryComponent} from '../../components/widgets/measure/measure-entry.component';
import {PrintEntryComponent} from '../../components/widgets/print/print-entry.component';
import {QueryEntryComponent} from '../../components/widgets/query/query-entry.component';
import {ReorderEntryComponent} from '../../components/widgets/reorder/reorder-entry.component';
import {TopomapsViewerEntryComponent} from '../../components/widgets/topomaps-viewer/topomaps-viewer-entry.component';
import {CheckLocalEntryComponent} from '../../components/widgets/checkLocal/checkLocal-entry.component';
import {FeaturetableEntryComponent} from '../../components/widgets/featuretable/featuretable-entry.component';
import {TraxsReportEntryComponent} from '../../components/widgets/traxsreport/traxsreport-entry.component';

/**
 * Service to control the dynamic grid object.
 * Keeps track of what the possible values of the grid are, and what is currently visible on the screen.
 * Allows widgets to turn widgets on/off as needed
 */
@Injectable({
  providedIn: 'root'
})
export class GridService {
  // The holder array of widget items that can be added to the screen
  private gridItems = [
    {cols: 10, rows: 23, y: 4, x: 1, name: 'toc', widget: TableOfContentsComponent},
    {cols: 7, rows: 17, y: 4, x: 1, name: 'addData', widget: AddDataEntryComponent},
    {cols: 11, rows: 16, y: 4, x: 1, name: 'identify', widget: IdentifyEntryComponent},
    {cols: 6, rows: 13, y: 4, x: 1, name: 'reorder', widget: ReorderEntryComponent},
    {cols: 35, rows: 21, y: 4, x: 1, name: 'attributes', widget: FeaturetableEntryComponent},
    {cols: 8, rows: 14, y: 4, x: 1, name: 'basemap', widget: BasemapGalleryEntryComponent},
    {cols: 10, rows: 16, y: 4, x: 1, name: 'query', widget: QueryEntryComponent},
    {cols: 8, rows: 23, y: 4, x: 1, name: 'legend', widget: LegendEntryComponent},
    {cols: 8, rows: 26, y: 4, x: 1, name: 'print', widget: PrintEntryComponent},
    {cols: 8, rows: 23, y: 4, x: 1, name: 'measurement', widget: MeasureEntryComponent},
    {cols: 10, rows: 9, y: 4, x: 1, name: 'draw', widget: DrawEntryComponent},
    {cols: 10, rows: 14, y: 4, x: 1, name: 'select', widget: SelectEntryComponent},
    {cols: 10, rows: 16, y: 4, x: 1, name: 'findlocation', widget: FindLocationEntryComponent},
    {cols: 12, rows: 12, y: 4, x: 1, name: 'feedback', widget: FeedbackEntryComponent},
    {cols: 6, rows: 13, y: 4, x: 1, name: 'checkLocal', widget: CheckLocalEntryComponent},
    {cols: 5, rows: 28, y: 4, x: 1, name: 'topomapsviewer', widget: TopomapsViewerEntryComponent},
    {cols: 13, rows: 18, y: 4, x: 1, name: 'traxsreport', widget: TraxsReportEntryComponent}
  ];
  // The widget items shown on the screen
  public gridItemsOn = [];
  private widgetChangeObs = new BehaviorSubject(null);

  constructor(private config: ConfigService, private ga: GoogleAnalyticsService) {}

  /**
   * Starts the grid up by adding any items that should be on to the grid array
   */
  public startGrid() {
    for (const tool of this.config.tools) {
      for (const item of this.gridItems) {
        if (item.name === tool.id) {
          if (tool.grid && !this.doesGridItemExist(tool.id)) {
            if (window.innerWidth > 800 && window.innerHeight > 400) {
              this.addGridItem(item);
            } else {
              this.widgetOff(item.name);
            }
          }
          break;
        }
      }
    }
    this.widgetChangeObs.next(true);
  }

  /**
   * Refreshs grid widgets. Used when new themes are loaded that may not have the same widgets or
   *  widget properties as the other themes in the app.
   */
  public refreshGrid() {
    if (this.gridItemsOn) {
      for (const tool of this.config.tools) {
        if (!this.doesGridItemExist(tool.id) && tool.grid && tool.status === 'widget-on') {       
          let gridItem = this.getGridItem(tool.id);
          if (gridItem) { 
            this.addGridItem(gridItem);
          }    
          }
        }
      }
      this.widgetChangeObs.next(true);
    }

  public getGrid() { return this.gridItemsOn; }
  public getGridList() { return this.gridItems; }
  private addGridItem(item) { this.gridItemsOn.push(item); }
  private removeGridItem(item) { this.gridItemsOn.splice(this.gridItemsOn.indexOf(item), 1); }
  public getWidgetChangeObs() { return this.widgetChangeObs.asObservable(); }
  private doesGridItemExist(name) {
    let widgetExistsFlag = false;
    for (const item of this.gridItemsOn) {
      if (item.name === name) {
        widgetExistsFlag = true;
        break;
      }
    }
    return widgetExistsFlag;
  }

  /**
   * Gets grid item by name
   * @param name 
   * @returns  
   */
  private getGridItem(name) {
    for (const item of this.gridItems) {
      if (item.name === name) {
        return item;
      }
    }
  }

  private activateWidgetBar(val, name) {
    for (const tool of this.config.tools) {
      if (tool.id === name) {
        tool.status = val ? 'widget-on' : 'widget-off';
        tool.grid = val;
        break;
      }
    }
    this.widgetChangeObs.next(true);
  }

  /**
   * Checks to see if a grid item is on
   * If not adds a grid item from the holder array to the active array to make it appear on screen
   * Then sends an event to Google Analytics to monitor on the dashboard
   * Then makes sure the widget bar icon is turned on
   * @param name: Name of the widget to activate
   */
  public async widgetOn(name) {
    if (!this.doesGridItemExist(name)) {
      for (const item of this.getGridList()) {
        if (item.name === name) {
          this.ga.eventEmitter('widget', 'select-content', item.name + ' on');
          this.addGridItem(item);
          break;
        }
      }
      this.activateWidgetBar(true, name);
    }
  }

  /**
   * Checks to see if the grid item is there
   * If exists is, then it removes a grid item from the holder array to the inactive array to make it appear on screen
   * Then sends an event to Google Analytics to monitor on the dashboard
   * Then makes sure that the widget bar icon is off
   * @param name: Name of the widget to remove
   */
  public async widgetOff(name) {
    if (this.doesGridItemExist(name)) {
      for (const item of this.getGridList()) {
        if (item.name === name) {
          this.ga.eventEmitter('widget', 'select-content', item.name + ' off');
          this.removeGridItem(item);
          break;
        }
      }
    }
    this.activateWidgetBar(false, name);
  }
}
